import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOrders } from "@/lib/api/hooks";
import { PageHeader } from "@/components/layout/page-header";
import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Order } from "@/lib/api/types";
import { ShoppingBag } from "lucide-react";

type OrderFilter = 'paid' | 'to_ship' | 'awaiting' | 'refund' | 'all';
const ORDER_FILTERS: { id: OrderFilter; label: string; match: (o: Order) => boolean }[] = [
    { id: 'paid', label: 'Paid', match: (o) => o.paymentStatus === 'paid' },
    { id: 'to_ship', label: 'To ship', match: (o) => o.paymentStatus === 'paid' && o.status === 'processing' },
    { id: 'awaiting', label: 'Awaiting payment', match: (o) => o.paymentStatus === 'pending' && o.status === 'pending' },
    { id: 'refund', label: 'Refund due', match: (o) => o.paymentStatus === 'refund_due' },
    { id: 'all', label: 'All', match: () => true },
];

export default function StoreOrdersPage() {
    const { orders, isLoading } = useOrders();
    const navigate = useNavigate();
    // Paid orders first: unpaid and abandoned checkouts used to be mixed in (ORD-12)
    const [filter, setFilter] = useState<OrderFilter>('paid');
    const counts = Object.fromEntries(ORDER_FILTERS.map(f => [f.id, (orders ?? []).filter(f.match).length]));
    const visibleOrders = (orders ?? []).filter(ORDER_FILTERS.find(f => f.id === filter)!.match);

    const formatPrice = (priceInCents: number) => {
        return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(priceInCents / 100);
    };

    const getStatusColor = (status: Order['status']) => {
        switch (status) {
            case 'pending': return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100';
            case 'processing': return 'bg-blue-100 text-blue-800 hover:bg-blue-100';
            case 'shipped': return 'bg-purple-100 text-purple-800 hover:bg-purple-100';
            case 'delivered': return 'bg-green-100 text-green-800 hover:bg-green-100';
            case 'cancelled': return 'bg-red-100 text-red-800 hover:bg-red-100';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getPaymentStatusColor = (status: Order['paymentStatus']) => {
        switch (status) {
            case 'paid': return 'bg-green-100 text-green-800 hover:bg-green-100';
            case 'pending': return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100';
            case 'failed': return 'bg-red-100 text-red-800 hover:bg-red-100';
            case 'refund_due': return 'bg-red-600 text-white hover:bg-red-600';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-muted-foreground">Loading orders...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title="Orders"
            />

            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter orders">
                {ORDER_FILTERS.map(f => (
                    <Button
                        key={f.id}
                        size="sm"
                        role="tab"
                        aria-selected={filter === f.id}
                        variant={filter === f.id ? 'default' : 'outline'}
                        className={f.id === 'refund' && counts.refund > 0 && filter !== f.id ? 'border-red-300 text-red-700' : undefined}
                        onClick={() => setFilter(f.id)}
                    >
                        {f.label} <span className="ml-1 opacity-70">({counts[f.id] ?? 0})</span>
                    </Button>
                ))}
            </div>

            {visibleOrders.length === 0 ? (
                <EmptyState
                    icon={ShoppingBag}
                    title={orders && orders.length > 0 ? "No orders here" : "No orders yet"}
                    description={orders && orders.length > 0 ? "Nothing matches this filter." : "Orders will appear here once customers make purchases"}
                />
            ) : (
                <div className="border rounded-lg bg-card overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Order #</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead>Customer</TableHead>
                                <TableHead>Total</TableHead>
                                <TableHead>Payment</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="w-[100px]"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {visibleOrders.map((order) => (
                                <TableRow key={order.id}>
                                    <TableCell className="font-medium">{order.orderNumber}</TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {format(new Date(order.createdAt), "MMM d, yyyy")}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex flex-col">
                                            <span className="font-medium">{order.customerName}</span>
                                            <span className="text-xs text-muted-foreground">{order.customerEmail}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="font-medium">{formatPrice(order.totalAmount)}</TableCell>
                                    <TableCell>
                                        <Badge variant="secondary" className={getPaymentStatusColor(order.paymentStatus)}>
                                            {order.paymentStatus.toUpperCase()}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="secondary" className={getStatusColor(order.status)}>
                                            {order.status.toUpperCase()}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => navigate(`/store/orders/${order.id}`)}
                                        >
                                            View
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </div>
    );
}
